import {
  FailureCauseError,
  nativeErrorCause,
  type ExperimentProduct,
  getSkuForPlatform,
  inferProductType,
} from '@forest/paywall-core'
import type { IAPServiceMethods } from '../services/iap'
import type { PaymentPlatform } from '../types'
import type { Product, SubscriptionProduct } from './types'

interface FourKeys {
  annualOriginal: Product
  annualEarlyBird: Product
  monthlyOriginal: Product
  monthlyEarlyBird: Product
}

/**
 * Fallback used on app versions below 5.2.0 that do not support
 * `getAvailableSubscriptionProducts`. Synthesizes `SubscriptionProduct[]` by
 * calling `getPrice` once per SKU so downstream merging is uniform.
 */
export async function getSubscriptionProductsBySkus(
  expSkus: ExperimentProduct[],
  platform: PaymentPlatform,
  iap: Pick<IAPServiceMethods, 'getPrice'>,
): Promise<SubscriptionProduct[]> {
  return Promise.all(
    expSkus.map(async (apiProduct) => {
      const sku = getSkuForPlatform(apiProduct, platform)
      const priceResult = await iap.getPrice({
        product_id: 0,
        product_type: 'subscription',
        sku,
      })
      if (!priceResult.success) {
        throw new FailureCauseError(
          nativeErrorCause(priceResult.error),
          priceResult.error,
        )
      }
      const { isAnnual } = inferProductType(apiProduct.title)
      return {
        sku,
        duration: isAnnual ? 'yearly' : 'monthly',
        eligible_trial_days: 0,
        price_localized: priceResult.data.price_localized,
        price_value: priceResult.data.price_value,
        display_name: '',
      }
    }),
  )
}

export function mergeProducts(
  expSkus: ExperimentProduct[],
  subscriptionProducts: SubscriptionProduct[],
  platform: PaymentPlatform,
): FourKeys {
  const nativeBySku = new Map<string, SubscriptionProduct>(
    subscriptionProducts.map((p) => [p.sku, p]),
  )
  const collected: Partial<FourKeys> = {}

  for (const apiProduct of expSkus) {
    const sku = getSkuForPlatform(apiProduct, platform)
    const native = nativeBySku.get(sku)
    if (!native) {
      throw new FailureCauseError(
        'sku_missing',
        `Product not found for SKU: ${sku}`,
      )
    }
    const { isAnnual, isEarlyBird } = inferProductType(apiProduct.title)
    const built: Product = {
      product_id: apiProduct.id,
      receipt_system_product_id: 0,
      sku,
      product_type: 'subscription',
      duration: native.duration,
      eligible_trial_days: native.eligible_trial_days,
      offers: native.offers,
      price_localized: native.price_localized,
      price_value: native.price_value,
      display_name: native.display_name,
    }
    if (isAnnual && isEarlyBird) collected.annualEarlyBird = built
    else if (isAnnual) collected.annualOriginal = built
    else if (isEarlyBird) collected.monthlyEarlyBird = built
    else collected.monthlyOriginal = built
  }

  if (
    !collected.annualOriginal ||
    !collected.annualEarlyBird ||
    !collected.monthlyOriginal ||
    !collected.monthlyEarlyBird
  ) {
    throw new FailureCauseError(
      'plan_missing',
      'Missing one of annual/monthly original/early-bird products',
    )
  }
  return collected as FourKeys
}
